import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { supabase } from "src/lib/supabase";
import JobKpiStrip from "./jobs/JobKpiStrip";
import JobAnalyticsPanel from "./jobs/JobAnalyticsPanel";
import JobsTable from "./jobs/JobsTable";
import JobDrawer from "./jobs/JobDrawer";
import JobBulkActionBar from "./jobs/JobBulkActionBar";

// Local replacement for the old mock isAtRisk — flags a job as at-risk if
// it's scheduled in the past but not yet completed/cancelled.
function isAtRisk(job) {
  if (!job.date || job.status === "completed" || job.status === "cancelled") return false;
  return new Date(job.date) < new Date();
}

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius:  { sm: 6, md: 8, lg: 12 },
  colors: {
    primary:   "#0F172A",
    secondary: "#334155",
    muted:     "#64748B",
    subtle:    "#94A3B8",
    border:    "#E2E8F0",
    bg:        "#F8FAFC",
    white:     "#FFFFFF"
  }
};

const PAGE_SIZE = 10;

const applyFilters = (jobs, searchQuery, activeKpi, filters, sortField, sortDir) => {
  let filtered = [...jobs];
  
  // Search
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(job => 
      job.jobId.toLowerCase().includes(q) ||
      job.customer.name.toLowerCase().includes(q) ||
      job.customer.phone.includes(q) ||
      (job.pro?.name || "").toLowerCase().includes(q) ||
      (job.pro?.phone || "").includes(q)
    );
  }
  
  // Status filter
  if (filters.status !== "all") {
    filtered = filtered.filter(job => job.status === filters.status);
  }
  
  // Category filter
  if (filters.category !== "all") {
    filtered = filtered.filter(job => job.category === filters.category);
  }
  
  // Payment status filter
  if (filters.paymentStatus !== "all") {
    filtered = filtered.filter(job => job.paymentStatus === filters.paymentStatus);
  }
  
  // Location filter
  if (filters.location !== "all") {
    filtered = filtered.filter(job => job.location.split(",")[0].trim().toLowerCase().includes(filters.location.toLowerCase()));
  }
  
  // Date range filter
  if (filters.dateRange !== "all") {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    
    filtered = filtered.filter(job => {
      const jobDate = job.date;
      if (filters.dateRange === "today") return jobDate === today;
      if (filters.dateRange === "week") return new Date(jobDate) >= weekAgo;
      if (filters.dateRange === "month") return new Date(jobDate) >= monthAgo;
      return true;
    });
  }
  
  // Price range filter
  if (filters.priceRange !== "all") {
    filtered = filtered.filter(job => {
      if (filters.priceRange === "0-1000") return job.price < 1000;
      if (filters.priceRange === "1000-5000") return job.price >= 1000 && job.price <= 5000;
      if (filters.priceRange === "5000+") return job.price > 5000;
      return true;
    });
  }
  
  // KPI filter
  if (activeKpi !== "all") {
    if (activeKpi === "in_progress") filtered = filtered.filter(job => job.status === "in_progress");
    else if (activeKpi === "completed") filtered = filtered.filter(job => job.status === "completed");
    else if (activeKpi === "disputed") filtered = filtered.filter(job => job.status === "disputed");
    else if (activeKpi === "pending_payment") filtered = filtered.filter(job => job.paymentStatus === "pending");
    else if (activeKpi === "cancelled") filtered = filtered.filter(job => job.status === "cancelled");
  }
  
  // Sort
  if (sortField) {
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortField === "jobId") { aVal = a.jobId; bVal = b.jobId; }
      else if (sortField === "date") { aVal = new Date(a.date); bVal = new Date(b.date); }
      else if (sortField === "price") { aVal = a.price; bVal = b.price; }
      else if (sortField === "paymentStatus") { aVal = a.paymentStatus; bVal = b.paymentStatus; }
      else if (sortField === "status") { aVal = a.status; bVal = b.status; }
      else { aVal = a.customer.name; bVal = b.customer.name; }
      
      if (aVal < bVal) return sortDir === 1 ? -1 : 1;
      if (aVal > bVal) return sortDir === 1 ? 1 : -1;
      return 0;
    });
  } else {
    // Default sort: at-risk first, then by date descending
    filtered.sort((a, b) => {
      const aRisk = isAtRisk(a);
      const bRisk = isAtRisk(b);
      if (aRisk && !bRisk) return -1;
      if (!aRisk && bRisk) return 1;
      return new Date(b.date) - new Date(a.date);
    });
  }
  
  return filtered;
};

export default function AdminJobs() {
  // Safe context destructuring (even though this component uses its own toast)
  const context = useOutletContext() || {};
  const { showToast: contextShowToast } = context;
  
  const [jobs, setJobs] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPro, setSelectedPro] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeKpi, setActiveKpi] = useState("all");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    category: "all",
    paymentStatus: "all",
    location: "all",
    dateRange: "all",
    priceRange: "all"
  });
  
  // Font injection
  useEffect(() => {
    const fonts = [
      "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&display=swap"
    ];
    fonts.forEach(href => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement("link");
        link.href = href;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
    });
  }, []);
  
  // Status mapping: job_requests.status only allows
  // draft/submitted/matched/confirmed/cancelled/completed (confirmed via
  // schema check). "Disputed" is not a job status — it's derived below from
  // an open reports row against the same booking, per the option-a decision.
  const REAL_TO_UI_STATUS = {
    draft: "requested",
    submitted: "requested",
    matched: "assigned",
    confirmed: "in_progress",
    completed: "completed",
    cancelled: "cancelled",
  };
  const UI_TO_REAL_STATUS = {
    requested: "submitted",
    assigned: "matched",
    in_progress: "confirmed",
    completed: "completed",
    cancelled: "cancelled",
  };

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("job_requests")
        .select(`
          id, description, status, payment_status, estimated_price,
          scheduled_time, location, urgency, created_at,
          customer:profiles!user_id ( full_name, phone ),
          pro:pro_profiles!pro_id ( id, category, profiles!user_id ( full_name, phone ) )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const jobIds = (data || []).map(j => j.id);
      let openReportsByBooking = {};
      if (jobIds.length > 0) {
        const { data: reportRows } = await supabase
          .from("reports")
          .select("id, booking_id, reason, description, status")
          .in("booking_id", jobIds)
          .in("status", ["pending", "reviewed"]);
        (reportRows || []).forEach(r => {
          openReportsByBooking[r.booking_id] = r; // most recent open report wins if multiple
        });
      }

      const shaped = (data || []).map(j => {
        const openReport = openReportsByBooking[j.id];
        return {
          id: j.id,
          jobId: j.id.slice(0, 8).toUpperCase(),
          customer: {
            name: j.customer?.full_name || "Unknown customer",
            phone: j.customer?.phone || "",
          },
          pro: j.pro ? {
            id: j.pro.id,
            name: j.pro.profiles?.full_name || "Unknown pro",
            phone: j.pro.profiles?.phone || "",
            category: j.pro.category || "",
            rating: 0, // real rating source not yet wired here — flagged, not guessed
          } : null,
          category: j.pro?.category || j.description || "Uncategorised",
          price: j.estimated_price || 0,
          status: openReport ? "disputed" : (REAL_TO_UI_STATUS[j.status] || j.status),
          paymentStatus: j.payment_status || "pending",
          date: j.scheduled_time ? j.scheduled_time.split("T")[0] : (j.created_at ? j.created_at.split("T")[0] : ""),
          location: j.location || "",
          dispute: openReport ? {
            reportId: openReport.id,
            raisedBy: "Reported",
            reason: openReport.reason,
            notes: openReport.description,
          } : null,
        };
      });

      setJobs(shaped);
    } catch (err) {
      console.error("Failed to load jobs:", err);
      showToast(`Failed to load jobs: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  
  // Memoized values
  const filteredJobs = useMemo(
    () => applyFilters(jobs, searchQuery, activeKpi, filters, sortField, sortDir),
    [jobs, searchQuery, activeKpi, filters, sortField, sortDir]
  );
  
  const paginatedJobs = useMemo(
    () => filteredJobs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredJobs, currentPage]
  );
  
  const totalPages = Math.ceil(filteredJobs.length / PAGE_SIZE);
  
  const categories = useMemo(
    () => ["all", ...new Set(jobs.map(j => j.category))],
    [jobs]
  );
  
  const locations = useMemo(
    () => ["all", ...new Set(jobs.map(j => j.location.split(",")[0].trim()))],
    [jobs]
  );
  
  // Handlers - using local toast, but could also use context toast if needed
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  
  const handleMarkComplete = useCallback(async (id) => {
    setActionLoading(true);
    const prevJobs = jobs;
    setJobs(prev => prev.map(j => j.id === id ? { ...j, status: "completed" } : j));
    if (selectedJob?.id === id) setSelectedJob(prev => ({ ...prev, status: "completed" }));
    try {
      const { error } = await supabase
        .from("job_requests")
        .update({ status: "completed" })
        .eq("id", id);
      if (error) throw error;
      showToast("Job marked as completed", "success");
    } catch (err) {
      console.error("Mark complete failed:", err);
      setJobs(prevJobs);
      showToast(`Failed to update: ${err.message}`, "error");
    } finally {
      setActionLoading(false);
    }
  }, [jobs, selectedJob, showToast]);

  const handleCancelJob = useCallback(async (id) => {
    if (!window.confirm("Cancel this job?")) return;
    setActionLoading(true);
    const prevJobs = jobs;
    setJobs(prev => prev.map(j => j.id === id ? { ...j, status: "cancelled" } : j));
    if (selectedJob?.id === id) setSelectedJob(prev => ({ ...prev, status: "cancelled" }));
    try {
      const { error } = await supabase
        .from("job_requests")
        .update({ status: "cancelled" })
        .eq("id", id);
      if (error) throw error;
      showToast("Job cancelled", "success");
    } catch (err) {
      console.error("Cancel failed:", err);
      setJobs(prevJobs);
      showToast(`Failed to cancel: ${err.message}`, "error");
    } finally {
      setActionLoading(false);
    }
  }, [jobs, selectedJob, showToast]);

  // Opening a dispute from the admin side creates a real reports row
  // against this booking, rather than a fake in-memory status flip.
  const handleOpenDispute = useCallback(async (id) => {
    const job = jobs.find(j => j.id === id);
    if (!job?.pro?.id) {
      showToast("Cannot open a dispute: no pro assigned to this job.", "error");
      return;
    }
    setActionLoading(true);
    try {
      const { data: { user: admin } } = await supabase.auth.getUser();
      const { data: newReport, error } = await supabase
        .from("reports")
        .insert({
          reporter_id: admin?.id,
          reported_pro_id: job.pro.id,
          booking_id: id,
          reason: "other",
          description: "Manually opened by admin",
          status: "pending",
        })
        .select()
        .single();
      if (error) throw error;

      const disputed = {
        reportId: newReport.id,
        raisedBy: "Admin",
        reason: "other",
        notes: "Manually opened by admin",
      };
      setJobs(prev => prev.map(j => j.id === id ? { ...j, status: "disputed", dispute: disputed } : j));
      if (selectedJob?.id === id) setSelectedJob(prev => ({ ...prev, status: "disputed", dispute: disputed }));
      showToast("Dispute opened", "error");
    } catch (err) {
      console.error("Open dispute failed:", err);
      showToast(`Failed to open dispute: ${err.message}`, "error");
    } finally {
      setActionLoading(false);
    }
  }, [jobs, selectedJob, showToast]);

  const handleRefundJob = useCallback(async (id) => {
    setActionLoading(true);
    const prevJobs = jobs;
    setJobs(prev => prev.map(j => j.id === id ? { ...j, paymentStatus: "refund_pending" } : j));
    if (selectedJob?.id === id) setSelectedJob({ ...selectedJob, paymentStatus: "refund_pending" });
    try {
      const { data, error } = await supabase.functions.invoke("mpesa-refund", { body: { booking_id: id } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      showToast("Refund initiated — awaiting M-Pesa confirmation", "success");
    } catch (err) {
      setJobs(prevJobs);
      showToast(`Refund failed: ${err.message}`, "error");
    }
    setActionLoading(false);
  }, [jobs, selectedJob, showToast]);

  const handleResolveDispute = useCallback(async (id) => {
    const job = jobs.find(j => j.id === id);
    setActionLoading(true);
    try {
      if (job?.dispute?.reportId) {
        const { error } = await supabase
          .from("reports")
          .update({ status: "resolved" })
          .eq("id", job.dispute.reportId);
        if (error) throw error;
      }
      const { error: jobErr } = await supabase
        .from("job_requests")
        .update({ status: "completed" })
        .eq("id", id);
      if (jobErr) throw jobErr;

      setJobs(prev => prev.map(j => j.id === id ? { ...j, status: "completed", dispute: null } : j));
      if (selectedJob?.id === id) setSelectedJob(prev => ({ ...prev, status: "completed", dispute: null }));
      showToast("Dispute resolved", "success");
    } catch (err) {
      console.error("Resolve dispute failed:", err);
      showToast(`Failed to resolve: ${err.message}`, "error");
    } finally {
      setActionLoading(false);
    }
  }, [jobs, selectedJob, showToast]);

  const handleCloseCase = useCallback(async (id) => {
    const job = jobs.find(j => j.id === id);
    setActionLoading(true);
    try {
      if (job?.dispute?.reportId) {
        const { error } = await supabase
          .from("reports")
          .update({ status: "dismissed" })
          .eq("id", job.dispute.reportId);
        if (error) throw error;
      }
      setJobs(prev => prev.map(j => j.id === id ? { ...j, dispute: null } : j));
      if (selectedJob?.id === id) setSelectedJob(prev => ({ ...prev, dispute: null }));
      showToast("Case closed", "success");
    } catch (err) {
      console.error("Close case failed:", err);
      showToast(`Failed to close case: ${err.message}`, "error");
    } finally {
      setActionLoading(false);
    }
  }, [jobs, selectedJob, showToast]);
  
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 1 ? -1 : 1);
    } else {
      setSortField(field);
      setSortDir(1);
    }
    setCurrentPage(1);
  }, [sortField]);
  
  const handleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }, []);
  
  const handleSelectAll = useCallback((checked, ids) => {
    if (checked) {
      setSelectedIds(new Set(ids));
    } else {
      setSelectedIds(new Set());
    }
  }, []);
  
  const handleBulkComplete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    const prevJobs = jobs;
    setJobs(prev => prev.map(j => ids.includes(j.id) ? { ...j, status: "completed" } : j));
    try {
      const { error } = await supabase.from("job_requests").update({ status: "completed" }).in("id", ids);
      if (error) throw error;
      showToast(`${ids.length} jobs completed`, "success");
    } catch (err) {
      setJobs(prevJobs);
      showToast(`Failed: ${err.message}`, "error");
    }
    setSelectedIds(new Set());
  }, [selectedIds, jobs, showToast]);

  const handleBulkCancel = useCallback(async () => {
    if (!window.confirm(`Cancel ${selectedIds.size} jobs?`)) return;
    const ids = Array.from(selectedIds);
    const prevJobs = jobs;
    setJobs(prev => prev.map(j => ids.includes(j.id) ? { ...j, status: "cancelled" } : j));
    try {
      const { error } = await supabase.from("job_requests").update({ status: "cancelled" }).in("id", ids);
      if (error) throw error;
      showToast(`${ids.length} jobs cancelled`, "success");
    } catch (err) {
      setJobs(prevJobs);
      showToast(`Failed: ${err.message}`, "error");
    }
    setSelectedIds(new Set());
  }, [selectedIds, jobs, showToast]);
  
  const handleBulkExport = useCallback(() => {
    const selectedJobs = jobs.filter(j => selectedIds.has(j.id));
    const headers = ["Job ID", "Customer Name", "Customer Phone", "Pro Name", "Category", "Price", "Status", "Payment Status", "Date"];
    const rows = selectedJobs.map(j => [
      j.jobId, j.customer.name, j.customer.phone, j.pro?.name || "Unassigned",
      j.category, j.price, j.status, j.paymentStatus, j.date
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jobs_export_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`Exported ${selectedIds.size} jobs`, "success");
  }, [jobs, selectedIds, showToast]);
  
  const handleCustomerClick = useCallback((job) => {
    setSelectedJob(null);
    setSelectedPro(null);
    setSelectedCustomer(job);
  }, []);
  
  const handleProClick = useCallback((job) => {
    setSelectedJob(null);
    setSelectedCustomer(null);
    setSelectedPro(job);
  }, []);
  
  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setActiveKpi("all");
    setFilters({
      status: "all",
      category: "all",
      paymentStatus: "all",
      location: "all",
      dateRange: "all",
      priceRange: "all"
    });
    setSortField(null);
    setSortDir(1);
    setCurrentPage(1);
  }, []);
  
  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <h1 style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 28,
        fontWeight: 800,
        color: UI.colors.primary,
        marginBottom: UI.spacing.xl
      }}>Jobs</h1>
      
      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <JobBulkActionBar
          selectedCount={selectedIds.size}
          onMarkComplete={handleBulkComplete}
          onCancelJobs={handleBulkCancel}
          onExport={handleBulkExport}
          onClear={() => setSelectedIds(new Set())}
        />
      )}
      
      {/* Toolbar */}
      {selectedIds.size === 0 && (
        <div style={{
          display: "flex",
          gap: UI.spacing.md,
          flexWrap: "wrap",
          marginBottom: UI.spacing.xl
        }}>
          <input
            type="text"
            placeholder="Search by job ID, customer, pro..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: "1 1 240px",
              maxWidth: 300,
              height: 40,
              padding: `0 ${UI.spacing.md}px`,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              outline: "none"
            }}
          />
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{
              height: 40,
              padding: `0 ${UI.spacing.md}px`,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              background: UI.colors.white,
              cursor: "pointer"
            }}
          >
            <option value="all">All Status</option>
            <option value="requested">Requested</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="disputed">Disputed</option>
          </select>
          
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            style={{
              height: 40,
              padding: `0 ${UI.spacing.md}px`,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              background: UI.colors.white,
              cursor: "pointer"
            }}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat === "all" ? "All Categories" : cat}</option>
            ))}
          </select>
          
          <select
            value={filters.paymentStatus}
            onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
            style={{
              height: 40,
              padding: `0 ${UI.spacing.md}px`,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              background: UI.colors.white,
              cursor: "pointer"
            }}
          >
            <option value="all">All Payments</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="refunded">Refunded</option>
          </select>
          
          <select
            value={filters.dateRange}
            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            style={{
              height: 40,
              padding: `0 ${UI.spacing.md}px`,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              background: UI.colors.white,
              cursor: "pointer"
            }}
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          
          <select
            value={filters.priceRange}
            onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
            style={{
              height: 40,
              padding: `0 ${UI.spacing.md}px`,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              background: UI.colors.white,
              cursor: "pointer"
            }}
          >
            <option value="all">All Prices</option>
            <option value="0-1000">Under KSh 1,000</option>
            <option value="1000-5000">KSh 1,000 – 5,000</option>
            <option value="5000+">Over KSh 5,000</option>
          </select>
          
          <button
            onClick={handleClearFilters}
            style={{
              height: 40,
              padding: `0 ${UI.spacing.lg}px`,
              background: UI.colors.primary,
              color: UI.colors.white,
              border: "none",
              borderRadius: UI.radius.md,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Clear Filters
          </button>
        </div>
      )}
      
      {/* KPI Strip */}
      <JobKpiStrip
        jobs={filteredJobs}
        activeKpi={activeKpi}
        onKpiClick={(key) => {
          setActiveKpi(key);
          setCurrentPage(1);
        }}
      />
      
      {/* Analytics Panel */}
      <JobAnalyticsPanel jobs={filteredJobs} />
      
      {/* Jobs Table */}
      <JobsTable
        jobs={paginatedJobs}
        allJobs={filteredJobs}
        selectedIds={selectedIds}
        onRowClick={setSelectedJob}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        sortField={sortField}
        sortDir={sortDir}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={PAGE_SIZE}
        totalPages={totalPages}
        loading={loading}
        onClearFilters={handleClearFilters}
        onMarkComplete={handleMarkComplete}
        onCancelJob={handleCancelJob}
        onOpenDispute={handleOpenDispute}
        onRefundJob={handleRefundJob}
        onCustomerClick={handleCustomerClick}
        onProClick={handleProClick}
      />
      
      {/* Job Drawer */}
      {selectedJob && (
        <JobDrawer
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onUpdate={(updated) => {
            setJobs(prev => prev.map(j => j.id === updated.id ? updated : j));
            setSelectedJob(updated);
          }}
          showToast={showToast}
          onCustomerClick={handleCustomerClick}
          onProClick={handleProClick}
          onMarkComplete={handleMarkComplete}
          onCancelJob={handleCancelJob}
          onRefundJob={handleRefundJob}
          onResolveDispute={handleResolveDispute}
          onCloseCase={handleCloseCase}
        />
      )}
      
      {/* Customer Mini-Drawer */}
      {selectedCustomer && (
        <>
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000
          }} onClick={() => setSelectedCustomer(null)} />
          <div style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: 440,
            background: UI.colors.white,
            boxShadow: "-4px 0 16px rgba(0,0,0,0.1)",
            zIndex: 1001,
            display: "flex",
            flexDirection: "column"
          }}>
            <div style={{
              height: 60,
              background: UI.colors.bg,
              borderBottom: `1px solid ${UI.colors.border}`,
              padding: `0 ${UI.spacing.xl}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 15,
                fontWeight: 700,
                color: UI.colors.primary
              }}>Customer Profile</div>
              <button onClick={() => setSelectedCustomer(null)} style={{
                width: 32,
                height: 32,
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.md,
                background: UI.colors.white,
                cursor: "pointer"
              }}>✕</button>
            </div>
            <div style={{ padding: UI.spacing.xl }}>
              <div style={{ display: "flex", alignItems: "center", gap: UI.spacing.md, marginBottom: UI.spacing.lg }}>
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#EEF2FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 20,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: "#4F46E5"
                }}>
                  {selectedCustomer.customer.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 16,
                    fontWeight: 700,
                    color: UI.colors.primary
                  }}>{selectedCustomer.customer.name}</div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                    color: UI.colors.muted
                  }}>{selectedCustomer.customer.phone}</div>
                </div>
              </div>
              <div style={{ marginBottom: UI.spacing.md }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  color: UI.colors.subtle,
                  marginBottom: UI.spacing.xs
                }}>LOCATION</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: UI.colors.secondary
                }}>{selectedCustomer.customer.location}</div>
              </div>
              <div style={{ marginBottom: UI.spacing.md }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  color: UI.colors.subtle,
                  marginBottom: UI.spacing.xs
                }}>TOTAL ORDERS</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: UI.colors.primary
                }}>{selectedCustomer.customer.totalOrders}</div>
              </div>
              <div style={{ marginBottom: UI.spacing.md }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  color: UI.colors.subtle,
                  marginBottom: UI.spacing.xs
                }}>TOTAL SPENT</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: UI.colors.primary
                }}>KSh {selectedCustomer.customer.totalSpend.toLocaleString()}</div>
              </div>
              <button
                onClick={() => {
                  showToast("Full profile in Customers tab", "info");
                  setSelectedCustomer(null);
                }}
                style={{
                  width: "100%",
                  padding: UI.spacing.md,
                  background: UI.colors.primary,
                  color: UI.colors.white,
                  border: "none",
                  borderRadius: UI.radius.md,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                View Full Profile →
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Pro Mini-Drawer */}
      {selectedPro && selectedPro.pro && (
        <>
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000
          }} onClick={() => setSelectedPro(null)} />
          <div style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: 440,
            background: UI.colors.white,
            boxShadow: "-4px 0 16px rgba(0,0,0,0.1)",
            zIndex: 1001,
            display: "flex",
            flexDirection: "column"
          }}>
            <div style={{
              height: 60,
              background: UI.colors.bg,
              borderBottom: `1px solid ${UI.colors.border}`,
              padding: `0 ${UI.spacing.xl}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 15,
                fontWeight: 700,
                color: UI.colors.primary
              }}>Pro Profile</div>
              <button onClick={() => setSelectedPro(null)} style={{
                width: 32,
                height: 32,
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.md,
                background: UI.colors.white,
                cursor: "pointer"
              }}>✕</button>
            </div>
            <div style={{ padding: UI.spacing.xl }}>
              <div style={{ display: "flex", alignItems: "center", gap: UI.spacing.md, marginBottom: UI.spacing.lg }}>
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#D1FAE5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 20,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: "#059669"
                }}>
                  {selectedPro.pro.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 16,
                    fontWeight: 700,
                    color: UI.colors.primary
                  }}>{selectedPro.pro.name}</div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                    color: UI.colors.muted
                  }}>{selectedPro.pro.phone}</div>
                </div>
              </div>
              <div style={{ marginBottom: UI.spacing.md }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  color: UI.colors.subtle,
                  marginBottom: UI.spacing.xs
                }}>CATEGORY</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: UI.colors.secondary
                }}>{selectedPro.pro.category}</div>
              </div>
              <div style={{ marginBottom: UI.spacing.md }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  color: UI.colors.subtle,
                  marginBottom: UI.spacing.xs
                }}>RATING</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#F59E0B"
                }}>★ {selectedPro.pro.rating}</div>
              </div>
              <button
                onClick={() => {
                  showToast("Full profile in Pros tab", "info");
                  setSelectedPro(null);
                }}
                style={{
                  width: "100%",
                  padding: UI.spacing.md,
                  background: UI.colors.primary,
                  color: UI.colors.white,
                  border: "none",
                  borderRadius: UI.radius.md,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                View Full Profile →
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 999,
          borderRadius: UI.radius.md,
          padding: "12px 20px",
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: UI.colors.white,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          background: toast.type === "success" ? "#0F172A" : toast.type === "error" ? "#DC2626" : "#3B82F6",
          animation: "fadeIn 0.2s ease"
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}